import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const ADMIN_ROLES = ["admin", "owner"];
const REGION = {
  Body: "Body", Beak: "Beak", Eyes_White: "Eye whites", Iris: "Eyes (iris)", Pupil: "Pupils", Brow: "Eyebrows",
  Hair: "Hair", Facial_Hair: "Beard", Outfit: "Outfit", Glasses: "Glasses", Teeth: "Teeth",
};
const pct = (s) => (s == null ? "—" : `${(100 * s).toFixed(s >= 0.1 ? 0 : 1)}%`);

// Admins (admin / owner) only: Step 3's per-color detail from the admin route, never part of the duck data a
// customer's page gets. Customers, partners and staff never call it.
export const useAdminPaintWarnings = (creation, wanted) => {
  const { user } = useAuth();
  const admin = ADMIN_ROLES.includes(user?.role);
  const [warnings, setWarnings] = useState(null);
  const id = creation?.id;
  const key = `${creation?.version}|${creation?.paint_fidelity?.reason}|${creation?.paint_health?.reason}`;
  useEffect(() => {
    if (!admin || !wanted || !id) return undefined;
    let live = true;
    api.get(`/dash/ducks/${id}/paint-warnings`)
      .then((r) => live && setWarnings(r.data || null))
      .catch(() => live && setWarnings(null));
    return () => { live = false; };
  }, [admin, wanted, id, key]);
  return admin && wanted ? warnings : null;
};

// "6 filaments · render shows 4" (a health failure) or "2 colors missing": admin facts line.
export const paintFacts = (w) => {
  if (!w) return null;
  const f = w.facts || {};
  if (w.source === "health" && f.n_filaments != null) {
    return `${f.n_filaments} filament${f.n_filaments === 1 ? "" : "s"} · render shows ${f.render_colours ?? "?"}`;
  }
  const n = w.colours?.length || 0;
  return n ? `${n} color${n === 1 ? "" : "s"} missing` : null;
};

const ColorRow = ({ c, testId }) => (
  <li data-testid={testId}>
    <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm border border-border align-middle" style={{ background: c.render_hex }} />
    <span className="font-semibold text-foreground">{REGION[c.region] || c.region}</span>{" "}
    <span className="font-mono2">{c.render_hex}</span>
    {` · ${pct(c.share)} of the render · ${c.rule}`}
    {c.nearest_name ? ` · nearest printed ${c.nearest_name}${c.nearest_de != null ? ` (dE ${c.nearest_de})` : ""}` : ""}
    {c.likely_shadow && (
      <>
        {" "}
        <span data-testid={`${testId}-shadow`} className="rounded-full bg-muted px-1.5 py-0.5 text-[10px]">
          likely shadow: dark, low-saturation, under the body
        </span>
      </>
    )}
  </li>
);

// The detail list: one row per missing render color, then every other reason (region, unexpected spool, eye ring).
export const PaintWarningsList = ({ warnings, testId = "paint-warnings" }) => {
  const colours = warnings?.colours || [];
  const other = warnings?.other || [];
  if (!colours.length && !other.length) return null;
  return (
    <ul data-testid={testId} className="mt-2 space-y-1 text-xs text-muted-foreground">
      {colours.map((c, i) => <ColorRow key={`${c.region}-${c.render_hex}`} c={c} testId={`${testId}-color-${i}`} />)}
      {other.map((w, i) => <li key={`${i}-${w}`} data-testid={`${testId}-other-${i}`}>{w}</li>)}
    </ul>
  );
};
